import { Directive, Output, EventEmitter, HostListener, Input } from '@angular/core';
import { Router } from '@angular/router';
import { BuildDetailsService } from '../build-details.service';

@Directive({
  selector: '[appGlobalDragDrop]',
  standalone: false
})
export class GlobalDragDropDirective {
  @Output() filesDropped = new EventEmitter<FileList>();
  @Output() dragging = new EventEmitter<boolean>();
  @Output() invalidFileType = new EventEmitter<string[]>(); // New output for invalid files
  @Input() acceptedFileTypes: string[] = ['.pdf', '.csv', '.xls', '.xlsx'];
  @Input() enabled: boolean = true; // Enable/disable directive

  private dragCounter = 0;

  constructor(private router: Router, private buildDetailsService: BuildDetailsService) {}

  private isRouteDisabled(): boolean {
    const disabledRoutes = (this.buildDetailsService.buildDetails as any).disableDragDropRoutes || [];
    return disabledRoutes.some((route: string) => this.router.url.includes(route));
  }

  private isEnabled(): boolean {
    return this.enabled && !this.isRouteDisabled();
  }

  @HostListener('window:dragenter', ['$event'])
  onDragEnter(event: DragEvent) {
    if (!this.isEnabled()) return;
    
    event.preventDefault();
    event.stopPropagation();

    const validationResult = this.validateDraggedFiles(event);
    
    if (validationResult === 'valid') {
      this.dragCounter++;
      if (this.dragCounter === 1) {
        this.dragging.emit(true);
      }
    }
  }

  @HostListener('window:dragover', ['$event'])
  onDragOver(event: DragEvent) {
    if (!this.isEnabled()) return;
    
    event.preventDefault();
    event.stopPropagation();

    const validationResult = this.validateDraggedFiles(event);
    
    if (event.dataTransfer) {
      event.dataTransfer.dropEffect = validationResult === 'valid' ? 'copy' : 'none';
    }
  }

  @HostListener('window:dragleave', ['$event'])
  onDragLeave(event: DragEvent) {
    if (!this.isEnabled()) return;
    
    event.preventDefault();
    event.stopPropagation();

    const validationResult = this.validateDraggedFiles(event);
    
    if (validationResult === 'valid') {
      this.dragCounter--;
      if (this.dragCounter === 0) {
        this.dragging.emit(false);
      }
    }
  }

  @HostListener('window:drop', ['$event'])
  onDrop(event: DragEvent) {
    if (!this.isEnabled()) return;
    
    event.preventDefault();
    event.stopPropagation();

    this.dragCounter = 0;
    this.dragging.emit(false);

    if (this.hasFiles(event) && event.dataTransfer?.files) {
      const files = event.dataTransfer.files;
      if (files.length > 0) {
        const validFiles = this.filterValidFiles(files);
        const invalidFileNames = this.getInvalidFileNames(files);
        
        // Only show error for invalid files, don't process anything
        if (invalidFileNames.length > 0) {
          this.invalidFileType.emit(invalidFileNames);
          // Don't process any files if there are invalid ones
          return;
        }
        
        if (validFiles.length > 0) {
          this.filesDropped.emit(this.createFileList(validFiles));
        }
      }
    }
  }

  /**
   * Validate dragged files - returns 'valid', 'invalid', or 'not-files'
   */
  private validateDraggedFiles(event: DragEvent): 'valid' | 'invalid' | 'not-files' {
    if (!event.dataTransfer) {
      return 'not-files';
    }

    // Check if it's a file drag at all
    const types = event.dataTransfer.types;
    if (!types.includes('Files')) {
      return 'not-files';
    }

    // Try to validate file extensions using items API
    if (event.dataTransfer.items && event.dataTransfer.items.length > 0) {
      let hasValidFile = false;
      let hasInvalidFile = false;
      
      for (let i = 0; i < event.dataTransfer.items.length; i++) {
        const item = event.dataTransfer.items[i];
        
        if (item.kind === 'file') {
          // Try to get file name for extension check (most reliable)
          const file = item.getAsFile();
          
          // Priority 1: Check by file extension if available
          if (file && file.name) {
            const extension = '.' + file.name.split('.').pop()?.toLowerCase();
            if (this.acceptedFileTypes.includes(extension)) {
              hasValidFile = true;
              continue;
            } else {
              // File name exists but extension is NOT accepted - this is invalid
              hasInvalidFile = true;
              break;
            }
          }
          
          // Priority 2: Check MIME type (fallback if file name not available)
          const type = item.type;
          if (type === 'application/pdf' || 
              type === 'text/csv' || 
              type === 'application/vnd.ms-excel' ||
              type === 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet') {
            hasValidFile = true;
            continue;
          }
          
          // Priority 3: No file name and unknown/empty MIME type - reject to be safe
          if (!file?.name && (!type || type === 'application/octet-stream' || type === '')) {
            hasInvalidFile = true;
            break;
          }
          
          // Priority 4: File name not available but MIME type is known but not supported
          if (!file?.name && type && type !== 'application/octet-stream') {
            hasInvalidFile = true;
            break;
          }
        }
      }
      
      // If we found any invalid file, reject
      if (hasInvalidFile) {
        return 'invalid';
      }
      
      // Only return valid if we found at least one valid file
      if (hasValidFile) {
        return 'valid';
      }
    }

    // Default to invalid for file drags with no valid files found
    return 'invalid';
  }

  /**
   * Check if drag event contains files (any type)
   */
  private hasFiles(event: DragEvent): boolean {
    if (!event.dataTransfer) {
      return false;
    }

    const types = event.dataTransfer.types;
    
    if (types.includes('Files')) {
      return true;
    }

    if (event.dataTransfer.items && event.dataTransfer.items.length > 0) {
      for (let i = 0; i < event.dataTransfer.items.length; i++) {
        if (event.dataTransfer.items[i].kind === 'file') {
          return true;
        }
      }
    }

    if (event.type === 'drop' && event.dataTransfer.files.length > 0) {
      return true;
    }

    if (types.includes('text/plain') || 
        types.includes('text/html') || 
        types.includes('text/uri-list')) {
      return types.includes('Files');
    }

    return false;
  }

  /**
   * Check if drag event contains VALID files (accepted types only)
   */
  private hasValidFiles(event: DragEvent): boolean {
    if (!this.hasFiles(event)) {
      return false;
    }

    // During drag, we can check file extensions via items API
    if (event.dataTransfer?.items) {
      let foundInvalidFile = false;
      let hasAnyFileItem = false;
      
      for (let i = 0; i < event.dataTransfer.items.length; i++) {
        const item = event.dataTransfer.items[i];
        if (item.kind === 'file') {
          hasAnyFileItem = true;
          
          // Try to get file info during drag (Chrome/Edge support this)
          const file = item.getAsFile();
          if (file && file.name) {
            const extension = '.' + file.name.split('.').pop()?.toLowerCase();
            if (!this.acceptedFileTypes.includes(extension)) {
              foundInvalidFile = true;
              break; // Found invalid file, reject immediately
            }
          }
          // If we can't get the file name, we can't validate, so allow it to proceed
        }
      }
      
      // Only reject if we found a definitively invalid file
      // If we have file items but couldn't validate, allow (will validate on drop)
      return hasAnyFileItem && !foundInvalidFile;
    }

    // Fallback for browsers that don't support items API during drag
    // Allow it to proceed, validation will happen on drop
    return true;
  }

  private filterValidFiles(fileList: FileList): File[] {
    const files: File[] = [];
    for (let i = 0; i < fileList.length; i++) {
      const file = fileList[i];
      const fileExtension = '.' + file.name.split('.').pop()?.toLowerCase();
      
      if (this.acceptedFileTypes.includes(fileExtension)) {
        files.push(file);
      }
    }
    return files;
  }

  private getInvalidFileNames(fileList: FileList): string[] {
    const invalidFiles: string[] = [];
    for (let i = 0; i < fileList.length; i++) {
      const file = fileList[i];
      const fileExtension = '.' + file.name.split('.').pop()?.toLowerCase();
      
      if (!this.acceptedFileTypes.includes(fileExtension)) {
        invalidFiles.push(file.name);
      }
    }
    return invalidFiles;
  }

  private createFileList(files: File[]): FileList {
    const dataTransfer = new DataTransfer();
    files.forEach(file => dataTransfer.items.add(file));
    return dataTransfer.files;
  }
}